import { useState, useRef, useEffect } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiX, FiMenu } from 'react-icons/fi';
import { expertService } from '../services/apiService';
import { getImageSrc } from '../utils/imageHelper';
import ExpertForm from './ExpertForm';

const ExpertList = ({ experts: initialExperts, fetchExperts }) => {
  const [experts, setExperts] = useState([]);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    setExperts([...initialExperts]);
    setOrderChanged(false);
  }, [initialExperts]);

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e, index) => {
    dragOverItem.current = index;
    e.preventDefault();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return;
    const updated = [...experts];
    const [dragged] = updated.splice(dragItem.current, 1);
    updated.splice(dragOverItem.current, 0, dragged);
    dragItem.current = null;
    dragOverItem.current = null;
    setExperts(updated);
    setOrderChanged(true);
    setSaveSuccess(false);
  };

  const handleDragEnd = () => {
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const saveOrder = async () => {
    setIsSaving(true);
    try {
      const items = experts.map((a, i) => ({ id: a._id, order: i }));
      await expertService.reorderExperts(items);
      setOrderChanged(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      fetchExperts();
    } catch (error) {
      console.error('Error saving order:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expert?')) {
      setIsDeleting(true);
      try {
        await expertService.deleteExpert(id);
        fetchExperts();
      } catch (error) {
        console.error('Error deleting expert:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  useEffect(() => {
    if (showForm && panelRef.current) {
      setTimeout(() => { panelRef.current.style.transform = 'translateX(0)'; }, 10);
    }
  }, [showForm]);

  const closePanel = () => {
    if (panelRef.current) {
      panelRef.current.style.transform = 'translateX(100%)';
      setTimeout(() => setShowForm(false), 300);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden relative">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Global Experts</h3>
          {orderChanged && (
            <button
              onClick={saveOrder}
              disabled={isSaving}
              className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {isSaving ? 'Saving…' : '💾 Save Order'}
            </button>
          )}
          {saveSuccess && (
            <span className="text-sm text-green-600 font-medium">✓ Order saved!</span>
          )}
        </div>
        <button
          onClick={() => { setSelectedExpert(null); setShowForm(true); }}
          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f]"
        >
          <FiPlus className="mr-1" /> Add Expert
        </button>
      </div>

      {orderChanged && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-700 text-xs">
          ↕ Drag rows to reorder. Click <strong>Save Order</strong> to apply changes to the website.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 w-8"></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name & Designation</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {experts.map((expert, index) => (
              <tr
                key={expert._id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
                className="hover:bg-gray-50 transition-colors cursor-default"
                style={{ opacity: dragItem.current === index ? 0.5 : 1 }}
              >
                <td className="px-3 py-4 w-8">
                  <FiMenu className="text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <img src={getImageSrc(expert.image)} alt={expert.name} className="h-12 w-12 object-cover rounded" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{expert.name}</div>
                  <div className="text-sm text-gray-500">{expert.designation}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{expert.location}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button onClick={() => { setSelectedExpert(expert); setShowForm(true); }} className="text-[#166534] hover:text-[#0e3d1f] mr-3"><FiEdit /></button>
                  <button onClick={() => handleDelete(expert._id)} disabled={isDeleting} className="text-red-500 hover:text-red-700"><FiTrash2 /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-500" onClick={closePanel} />
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <div ref={panelRef} className="relative w-screen max-w-md transform transition-all duration-300" style={{ transform: 'translateX(calc(100% + 20px))', marginLeft: '-20px' }}>
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">{selectedExpert ? 'Edit Expert' : 'Add New Expert'}</h3>
                    <button onClick={closePanel} className="text-gray-400 hover:text-gray-500"><FiX className="h-6 w-6" /></button>
                  </div>
                  <div className="flex-1 p-4">
                    <ExpertForm expert={selectedExpert} onClose={closePanel} onSave={() => { fetchExperts(); closePanel(); }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertList;
